import { Component, inject, Input } from '@angular/core';
import { LocationResponseInterface } from "../models/store";
import { StructuredDataLocationInterface } from "./models";
import { StructuredDataAbstractComponent } from "./structured-data.abstract.component";
import { EnvService } from "@gea-waldviertler-website/shop-lib";

@Component({
  selector: 'gea-shop-structured-data-location',
  templateUrl: './structured-data.component.html',
  styleUrls: [],
  standalone: false
})
export class StructuredDataLocationComponent extends StructuredDataAbstractComponent {
  @Input({required: true}) location?: LocationResponseInterface

  openingHours: string[][] | undefined[] = []
  breakHours: string[][] | undefined[] = []
  weekDays = [
    'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'
  ]
  type: "ShoeStore" | "FurnitureStore" = "ShoeStore"
  private envService = inject(EnvService)

  protected generateJsonLd(): StructuredDataLocationInterface | undefined {
    if (! this.location) {
      console.warn("No location given, do not render structured data")
      return undefined
    }

    this.location.services.forEach((service) => {
      if (service.primary && service.service == "Möbelgeschäft") {
        this.type = "FurnitureStore"
      }
    })

    this.location.hours.forEach((str, index) => {
      if (! str) {
        this.breakHours[index] = undefined
        this.openingHours[index] = undefined
      } else {
        const split = str.split(", ")
        let openStart, openEnd, breakStart, breakEnd = undefined

        // has break
        if (split.length == 2) {
          const firstSplit = split[0].split("-")
          const lastSplit = split[1].split("-")
          openStart = firstSplit[0]
          openEnd = lastSplit[1]
          breakStart = firstSplit[1]
          breakEnd = lastSplit[0]
        } else { // no break
          const wholeSplit = str.split("-")
          openStart = wholeSplit[0]
          openEnd = wholeSplit[1]
        }
        this.openingHours[index] = [ openStart, openEnd ]
        if (!breakStart || !breakEnd) {
          this.breakHours[index] = undefined
        } else {
          this.breakHours[index] = [ breakStart, breakEnd ]
        }
      }
    })

    let data: StructuredDataLocationInterface = {
      "@context": "http://www.schema.org",
      "@type": this.type,
      "name": this.location.name,
      "priceRange": "$$$$",
    }

    if (this.location.mainImageSlug) {
      data.image = this.envService.config.imagesURL + '/' + this.location.mainImageSlug + '.webp'
    }

    if (this.location.text) {
      data.description = this.location.text
    }

    if (this.location.address) {
      data.address = {
        "@type": "PostalAddress",
        streetAddress: this.location.address.street + (this.location.address.houseNumber ? ' ' + this.location.address.houseNumber : ''),
        addressLocality: this.location.address.city,
        postalCode: this.location.address.postalcode,
        addressCountry: this.location.address.country?.code ?? 'AT',
      }
    }

    if (this.location.phone) {
      data.telephone = this.location.phone
    }

    if (this.openingHours) {
      data.openingHoursSpecification = []
      this.openingHours.forEach((openingHours, index) => {
        if (openingHours) {
          data.openingHoursSpecification!.push({
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": "https://schema.org/" + this.weekDays[index],
            "opens": openingHours[0],
            "closes": openingHours[1]
          })
        }
      })
    }

    if (this.breakHours) {
      data.specialOpeningHoursSpecification = []
      this.breakHours.forEach((breakHours, index) => {
        if (breakHours) {
          data.specialOpeningHoursSpecification!.push({
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": "https://schema.org/" + this.weekDays[index],
            "opens": breakHours[1],
            "closes": breakHours[0]
          })
        }
      })
    }

    if (this.location.address.lng && this.location.address.lat) {
      data.geo = {
        "@type": "GeoCoordinates",
        latitude: this.location.address.lat,
        longitude: this.location.address.lng
      }
    }

    return data
  }
}
